import {
  Box,
  Button,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  Textarea,
  VStack
} from "@chakra-ui/react";
import axios from "axios";
import { CustomModal } from "components/custom-modal";
import { EmpresasEndpoints } from "components/empresas-components/utils";
import { InputSearch } from "components/input-search";
import { CloseIcon } from "components/vectors/close-icon";
import { LogoutIcon } from "components/vectors/logout-icon";
import { SendIcon2 } from "components/vectors/send-icon";
import { UserIcon } from "components/vectors/user-icon";
import { useApplicationContext } from "contexts/ApplicationContext";
import { UserTypes } from "contexts/types/application-context-types";
import { apiJWT } from "api/api-jwt";
import { useState } from "react";
import { useRouter } from "router-manager/parts";

const USER_TYPES: UserTypes[] = [
  "SUPER",
  "REGIONAL",
  "COMERCIAL",
  "PARCEIRO_MASTER",
  "PARCEIRO_VENDEDOR",
  "PARCEIRO_OPERACIONAL",
  "FINANCEIRO",
  "ASCOM",
  "PARCEIRO_GERENTE_ESTRATEGICO",
  "PARCEIRO_GERENTE_VAREJO"
];

type TipoTarget = "ALL" | "CUSTOMER" | "CUSTOMER_USER" | "CUSTOMER_USER_TYPE";

interface EmpresaOption {
  id: number;
  name: string;
  codigoNova: string;
}

interface UsuarioOption {
  id: number;
  name: string;
}

interface NotificationForm {
  titulo: string;
  mensagem: string;
  tipoEnvio: string;
  tipoTarget: TipoTarget | "";
  targetCustomerId: number | null;
  targetUserId: number | null;
  targetUserType: UserTypes | null;
  expiresAt: string;
}

const INITIAL_FORM: NotificationForm = {
  titulo: "",
  mensagem: "",
  tipoEnvio: "",
  tipoTarget: "",
  targetCustomerId: null,
  targetUserId: null,
  targetUserType: null,
  expiresAt: ""
};

export function ProfileMenu() {
  const { user, setUserAction, menuErrorMessage } = useApplicationContext();
  const { userData } = user;
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState<NotificationForm>(INITIAL_FORM);
  const [isSending, setIsSending] = useState(false);
  const [empresas, setEmpresas] = useState<EmpresaOption[]>([]);
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [usuarios, setUsuarios] = useState<UsuarioOption[]>([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);

  const onClose = () => {
    setIsOpen(false);
    setForm(INITIAL_FORM);
    setEmpresas([]);
    setUsuarios([]);
  };
  const onOpen = () => setIsOpen(true);

  const isNotification =
    user.userData.type === "SUPER" &&
    user.userData.customerId === 1 &&
    [1, 7215, 11031].includes(userData.userId!) &&
    user?.userData.authorities?.includes("NOTIFICATION_WRITE");

  const showEmpresa =
    form.tipoTarget === "CUSTOMER" ||
    form.tipoTarget === "CUSTOMER_USER" ||
    form.tipoTarget === "CUSTOMER_USER_TYPE";

  const handleTipoTargetChange = async (value: TipoTarget | "") => {
    setForm((prev) => ({
      ...prev,
      tipoTarget: value,
      targetCustomerId: null,
      targetUserId: null,
      targetUserType: null
    }));
    setUsuarios([]);

    if (
      value === "CUSTOMER" ||
      value === "CUSTOMER_USER" ||
      value === "CUSTOMER_USER_TYPE"
    ) {
      setLoadingEmpresas(true);
      try {
        const data = await EmpresasEndpoints.getEmpresas();
        setEmpresas(data);
      } catch {
        setEmpresas([]);
      } finally {
        setLoadingEmpresas(false);
      }
    } else {
      setEmpresas([]);
    }
  };

  const handleEmpresaChange = async (customerId: number | null) => {
    setForm((prev) => ({
      ...prev,
      targetCustomerId: customerId,
      targetUserId: null
    }));
    setUsuarios([]);

    if (form.tipoTarget === "CUSTOMER_USER" && customerId !== null) {
      setLoadingUsuarios(true);
      try {
        const data = await EmpresasEndpoints.getUsuariosByCustomer(customerId);
        setUsuarios(data);
      } catch {
        setUsuarios([]);
      } finally {
        setLoadingUsuarios(false);
      }
    }
  };

  const handleSubmit = async () => {
    console.log("Enviando notificação com dados:", form);
    // return;

    // const baseUrl = "http://localhost:8080/api";
    // const accessToken = apiJWT.getAccessToken();
    // if (!accessToken) return;

    setIsSending(true);
    try {
      await apiJWT.post(`/notifications/`, {
        mensagem: form.mensagem,
        titulo: form.titulo,
        tipoEnvio: form.tipoEnvio,
        tipoTarget: form.tipoTarget,
        targetCustomerId: form.targetCustomerId,
        targetUserId: form.targetUserId,
        targetUserType: form.targetUserType,
        expiresAt: form.expiresAt
      });
      onClose();
    } catch (e) {
      console.error("Erro ao enviar notificação", e);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Menu>
      <MenuButton
        as={IconButton}
        bg="bg-gray"
        borderRadius="full"
        w="45px"
        h="45px"
        isDisabled={!!menuErrorMessage}
      >
        <UserIcon stroke="custom-gray" />
      </MenuButton>
      <MenuList py="0" overflow="hidden" zIndex={100}>
        {isNotification && (
          <MenuItem
            icon={<SendIcon2 width="15px" height="15px" />}
            _hover={{ bg: "gray.100" }}
            onClick={onOpen}
          >
            Enviar Notificação
          </MenuItem>
        )}
        <MenuItem
          icon={<LogoutIcon width="15px" height="15px" />}
          _focus={{}}
          onClick={() => {
            setUserAction({ type: "logout" });
            router.push("/login");
          }}
          _hover={{ bg: "gray.100" }}
        >
          Sair
        </MenuItem>
      </MenuList>

      <CustomModal
        isOpen={isOpen}
        onClose={onClose}
        modalTitle="Enviar Notificação"
        size="md"
        modalFooter={
          <Box display="flex" gap="10px">
            <Button
              leftIcon={<SendIcon2 />}
              onClick={handleSubmit}
              isLoading={isSending}
              loadingText="Enviando..."
              isDisabled={
                !form.tipoEnvio ||
                !form.tipoTarget ||
                !form.mensagem ||
                !form.titulo ||
                !form.expiresAt
              }
            >
              Enviar
            </Button>
            <Button
              variant="outline"
              colorScheme="red"
              onClick={onClose}
              leftIcon={<CloseIcon />}
            >
              Fechar
            </Button>
          </Box>
        }
      >
        <VStack spacing="12px" py="8px">
          <FormControl isRequired>
            <FormLabel fontSize="13px">Tipo de Envio</FormLabel>
            <Select
              size="sm"
              placeholder="Selecione"
              value={form.tipoEnvio}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, tipoEnvio: e.target.value }))
              }
            >
              <option value="NOTIFICATION">Notificação</option>
              <option value="ALERT">Alerta</option>
            </Select>
          </FormControl>

          <FormControl isRequired>
            <FormLabel fontSize="13px">Target</FormLabel>
            <Select
              size="sm"
              placeholder="Selecione"
              value={form.tipoTarget}
              onChange={(e) =>
                handleTipoTargetChange(e.target.value as TipoTarget | "")
              }
            >
              <option value="ALL">Todos</option>
              <option value="CUSTOMER">Empresa</option>
              <option value="CUSTOMER_USER">Usuário da Empresa</option>
              <option value="CUSTOMER_USER_TYPE">
                Tipo de Usuário da Empresa
              </option>
            </Select>
          </FormControl>

          {showEmpresa && (
            <FormControl isRequired>
              <FormLabel fontSize="13px">Empresa</FormLabel>
              <InputSearch
                value={
                  empresas.find((e) => e.id === form.targetCustomerId)?.name ??
                  ""
                }
                onChange={(value) => handleEmpresaChange(value as number)}
                options={empresas.map(({ id, name, codigoNova }) => ({
                  name,
                  codigoNova,
                  value: id
                }))}
                searchKeys={["name", "codigoNova"]}
                optionLabelRender={(option) =>
                  option.codigoNova
                    ? `${option.codigoNova} - ${option.name}`
                    : option.name
                }
                checkIsInclude={(option) =>
                  option.value === form.targetCustomerId
                }
                inputProps={{
                  placeholder: loadingEmpresas
                    ? "Carregando..."
                    : "Buscar por nome ou código Nova",
                  isDisabled: loadingEmpresas
                }}
                openOnFocus
                partialSearch
                closeOnSelect
              />
            </FormControl>
          )}

          {form.tipoTarget === "CUSTOMER_USER" && (
            <FormControl isRequired>
              <FormLabel fontSize="13px">Usuário</FormLabel>
              <Select
                size="sm"
                placeholder={
                  !form.targetCustomerId
                    ? "Selecione uma empresa primeiro"
                    : loadingUsuarios
                      ? "Carregando..."
                      : "Selecione o usuário"
                }
                isDisabled={!form.targetCustomerId || loadingUsuarios}
                value={form.targetUserId ?? ""}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    targetUserId: e.target.value ? Number(e.target.value) : null
                  }))
                }
              >
                {usuarios.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </Select>
            </FormControl>
          )}

          {form.tipoTarget === "CUSTOMER_USER_TYPE" && (
            <FormControl isRequired>
              <FormLabel fontSize="13px">Tipo Usuário</FormLabel>
              <Select
                size="sm"
                placeholder="Selecione"
                value={form.targetUserType!}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    targetUserType: e.target.value as UserTypes
                  }))
                }
              >
                {USER_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl isRequired>
            <FormLabel fontSize="13px">Expiração</FormLabel>
            <Input
              size="sm"
              type="datetime-local"
              value={form.expiresAt}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, expiresAt: e.target.value }))
              }
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel fontSize="13px">Título</FormLabel>
            <Input
              size="sm"
              placeholder="Título da notificação"
              value={form.titulo}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, titulo: e.target.value }))
              }
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel fontSize="13px">Mensagem</FormLabel>
            <Textarea
              size="sm"
              placeholder="Mensagem da notificação"
              rows={4}
              value={form.mensagem}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, mensagem: e.target.value }))
              }
            />
          </FormControl>
        </VStack>
      </CustomModal>
    </Menu>
  );
}
